/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'../../../filters/FilterSpecHelper',
	'./api/QueryDefinitionModifierAPI',
	'./DataQueryUtils'
], (APIFactory, ContentUtil, FilterSpecHelper, QueryDefinitionModifierAPI, DataQueryUtils) => {
	/**
	* The visualization model is not ready to support brushing (highlight and filters) in R5
	* The purpose of the class is to wired off generating brushing as query filters for expanded column in R5v
	* This is a temporary solution to R5 to wired off expoanded columns brushing support until R6
	*/

	class CrosstabQueryDefinitionModifier {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			features.DataQueryExecution.registerQueryDefinitionModifier(this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryDefinitionModifierAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		modifyQueryDefinitionList(definitions) {
			const vis = this.content.getFeature('Visualization');
			if (vis.getType() === 'Crosstab') {
				definitions.forEach(definition => {
					const filters = definition.getFilters();
					const brushedFilters = filters.filter(filter => {
						return !!filter.visualizationId;
					});
					definition.removeFilters(brushedFilters);
					definition.setSupportsNonContextualTopBottom(false);
					this._modifyFilters(definition);
				});
			}
			return definitions;
		}

		_modifyFilters(definition) {
			const filterSpecList = [];
			const visualization = this.content.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			const brushedFilters = this._getBrushedFiltersByVisualization();
			const unionedDataItemList = this._getUnionedDataItemList(visualization);

			if (unionedDataItemList.length > 0) {
				brushedFilters.forEach(brushedFilter => {
					let addFilter = true;
					const columnIds = brushedFilter.columnId ? [brushedFilter.columnId] : FilterSpecHelper.getSelectedCompoundFilterColumnIds([brushedFilter]);
					for (let index = 0; index < columnIds.length; index++) {
						const brushedColumn = this._getHierarchyMetadatColumn(dataSource, dataSource.getMetadataColumn(columnIds[index]));
						if (brushedColumn) {
							const expandedColumn = unionedDataItemList.find(dataItem => {
								const column = this._getHierarchyMetadatColumn(dataSource, dataItem.getMetadataColumn());
								return column && brushedColumn.getId() === column.getId();
							});
							if (expandedColumn) {
								//One of the columnId in the brushed filter is defined in the expanded column so do not include it
								addFilter = false;
								break;
							}
						}
					}
					if (addFilter) {
						filterSpecList.push(brushedFilter);
					}
				});
			} else {
				filterSpecList.push(...brushedFilters);
			}

			const updatedFilterSpecList = DataQueryUtils.convertExcludeEmptyValuesFilters(filterSpecList, visualization.getDataSource());
			definition.addFilters(updatedFilterSpecList);
		}

		_getBrushedFiltersByVisualization () {
			const eventGroups = this.dashboard.getFeature('EventGroups');
			const globalFilters = this.dashboard.getFeature('GlobalFilters');
			const visualization = this.content.getFeature('Visualization');
			const filterQuerySpecList = [];
			const dataSource = visualization.getDataSource();
			const type = this.dashboard.getAppConfig('pageContainerType');
			const page = ContentUtil.getPageContent(this.content, type);
			const pageId = page && page.getId();

			if (dataSource && visualization.getDefinition().getProperty('reactToExternalBrushing') !== false) {
				const eventGroupId = eventGroups.getGroupId(this.content.getId());
				filterQuerySpecList.push(...globalFilters.getFilterList({
					origin: 'visualization',
					sourceId: dataSource.getId(),
					scope: pageId,
					eventGroupId: eventGroupId
				}, this.content.getId()));
			}
			// Exclude binning filters since they are not fully supported by query service.
			return filterQuerySpecList.filter(filterSpec => !filterSpec.filterBins);
		}

		_getHierarchyMetadatColumn (dataSource, column) {
			return (!column || column.isHierarchy()) ? column : this._getHierarchyMetadatColumn(dataSource, column.getParent());
		}

		_getUnionedDataItemList (visualization) {
			const unionedDataItemList = [];
			const dataItemList = visualization.getSlots().getDataItemList();
			dataItemList.forEach(dataItem => {
				unionedDataItemList.push(...dataItem.getUnion().getDataItemList());
			});
			return unionedDataItemList;
		}

	}
	return CrosstabQueryDefinitionModifier;
});
